/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { AsyncPipe, CommonModule, NgIf } from '@angular/common';
import { Component, DestroyRef, OnInit } from '@angular/core';
import { AccordionArticleFilterInfoBlockComponent } from '@domgen/dgx-fe-components/accordion';
import { ArticleSearchResults } from '@domgen/dgx-fe-components/content/lib/models/article-search-results.interface';
import { VideoSearchResults } from '@domgen/dgx-fe-components/content/lib/models/video-search-results.interface';
import {
  GridColumnComponent,
  GridContainerComponent,
  GridRowComponent,
  GridSectionComponent,
} from '@domgen/dgx-fe-components/grid';
import { extractPageNameFromUrl } from '@domgen/dgx-fe-ga';
import { TranslatePipe } from '@domgen/dgx-fe-i18n';
import { SeoPage } from '@domgen/dgx-fe-seo';
import { ArticleSearchService } from '@self-fix/data-access';
import {
  ArticleFilterComponent,
  ArticleFilterComponentService,
} from '@self-fix/feature-components';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  map,
  of,
  take,
  tap,
} from 'rxjs';
import { provideArticleSearchI18n } from '../../providers/article-search-i18n.provider';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { SmartFixHubAnalyticsService } from 'libs/self-fix/data-access/src/lib/services/sf-hub-analytics/sf-hub-analytics-service';
import { KeywordSearchResultsBlockComponent } from '../../components/keyword-search-results-block/keyword-search-results-block.component';

interface ViewModel {
  articles: ArticleSearchResults;
  categories: string[];
  quantity: number;
  searchTerm?: string;
  subcategories: string[];
  videos: VideoSearchResults;
}

@Component({
  standalone: true,
  imports: [
    ArticleFilterComponent,
    AsyncPipe,
    GridColumnComponent,
    GridContainerComponent,
    GridRowComponent,
    GridSectionComponent,
    KeywordSearchResultsBlockComponent,
    NgIf,
    TranslatePipe,
    AccordionArticleFilterInfoBlockComponent,
    CommonModule,
  ],
  host: { ngSkipHydration: 'true' },
  templateUrl: './article-search.component.html',
  styleUrls: ['./article-search.component.scss'],
  providers: [provideArticleSearchI18n(), ArticleFilterComponentService],
})
@SeoPage()
export class ArticleSearchComponent implements OnInit {
  readonly isSearchSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly articleSearchService: ArticleSearchService,
    private readonly destroyRef: DestroyRef,
    private readonly route: ActivatedRoute,
    private readonly smartFixAnalytics: SmartFixHubAnalyticsService
  ) {}

  ngOnInit(): void {
    this.isSearchSubject.next(extractPageNameFromUrl().includes('search'));

    this.route.queryParamMap
      .pipe(
        map((queryParamMap) => {
          const param = queryParamMap.get('term') ?? undefined;
          return param;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((searchTerm) => {
        this.smartFixAnalytics.trackEvent({
          eventCategory: 'sf-hub',
          eventAction: 'search box - usage',
          pagename: 'claims-sf-hub-dashboard',
          eventLabel: searchTerm,
          event: 'genericGAEvent',
        });

        if (searchTerm && searchTerm.length >= 3) {
          this.articleSearchService
            .getArticles(searchTerm, 50)

            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
          this.articleSearchService
            .getVideos(searchTerm, 50)

            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        }
      });
  }

  selectedVideos$: Observable<VideoSearchResults> =
    this.articleSearchService.selectedVideos$.pipe(
      map((videos) => ({
        title: 'Video Guides',
        videos: videos,
        viewMoreLink: {
          href: 'videos',
        },
      }))
    );

  selectedArticles$: Observable<ArticleSearchResults> =
    this.articleSearchService.articles$.pipe(
      map((selectedArticles) => ({
        title: 'Articles',
        articles: selectedArticles,
        viewMoreLink: {
          href: 'articles',
        },
      }))
    );

  readonly vm$: Observable<ViewModel> = combineLatest({
    articles: this.selectedArticles$,
    videos: this.selectedVideos$,
    searchTerm: this.articleSearchService.searchTerm$,
    categories: this.articleSearchService.selectedCategories$,
    subcategories: this.articleSearchService.selectedSubcategories$,
    quantity: this.articleSearchService.quantity$,
  });
}
